<template>
  <el-tree-select
    v-model="selectedValue"
    :data="treeData"
    :props="defaultProps"
    :multiple="multiple"
    :collapse-tags="multiple"
    :collapse-tags-tooltip="multiple"
    :clearable="clearable"
    :placeholder="placeholder"
    class="category-selector"
    :disabled="disabled"
    :loading="loading"
    node-key="id"
    show-checkbox
    :check-strictly="checkStrictly"
    filterable
    :filter-method="filterNode"
    :render-after-expand="false"
  />
</template>

<script>
import axios from 'axios';
import { BACKEND_URL } from '../../../share-source/src/config/config.js';

export default {
  name: 'CategorySelector',
  props: {
    modelValue: {
      type: [Number, Array],
      default: null
    },
    multiple: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'Select Category'
    },
    clearable: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    hideDisabled: {
      type: Boolean,
      default: true
    },
    checkStrictly: {
      type: Boolean,
      default: false
    },
    maxHeight: {
      type: String,
      default: '400px'
    },
    initialCategories: {
      type: Array,
      default: function() {
        return [];
      }
    },
    autoFetch: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      categories: [],
      loading: false,
      treeData: [],
      defaultProps: {
        children: 'children',
        label: 'name',
        disabled: 'disabled',
        value: 'id'
      },
      filterText: '',
      changeTimer: null
    };
  },
  computed: {
    selectedValue: {
      get() {
        // Initialize for multiple mode if needed
        if (this.multiple && this.modelValue === null) {
          return [];
        }
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
        
        // Debounce the change event
        clearTimeout(this.changeTimer);
        this.changeTimer = setTimeout(() => {
          this.$emit('change', value);
        }, 300);
      }
    }
  },
  watch: {
    categories: {
      handler(newCategories) {
        this.buildTreeData(newCategories);
      },
      deep: true
    },
    initialCategories: {
      handler(newCategories) {
        if (newCategories && newCategories.length > 0) {
          this.categories = newCategories;
        }
      },
      deep: true
    }
  },
  mounted() {
    // Initialize data based on props
    // autoFetch and initialCategories can't be combined, since initialCategories has two different values,
    // one is before parent component is mounted, which is null, and the other is after it is mounted, which is the initialCategories .
    if (this.autoFetch) {
      setTimeout(() => {
        this.fetchCategories();
      }, 200);
    } else if (this.initialCategories && this.initialCategories.length > 0) {
      // console.log("initialCategories", this.initialCategories);
      this.categories = this.initialCategories;
    }
  },
  methods: {
    async fetchCategories() {
      this.loading = true;
      try {
        const response = await axios.get(`${BACKEND_URL}/api/category/all/active`, {
          withCredentials: true
        });
        this.categories = response.data;
      } catch (error) {
        console.error("Error fetching categories:", error);
        this.$emit('fetch-error', error);
      } finally {
        this.loading = false;
      }
    },
    buildTreeData(categories) {
      if (!categories || categories.length === 0) {
        this.treeData = [];
        return;
      }
      
      // Filter out disabled categories if hideDisabled is true
      const filteredCategories = this.hideDisabled 
        ? categories.filter(category => category.enabled) 
        : categories;
      
      // Create a map for quick lookup
      const categoryMap = {};
      filteredCategories.forEach(category => {
        // Create a new object with the properties we need
        categoryMap[category.id] = {
          id: category.id,
          name: category.name,
          disabled: !category.enabled,
          children: [],
          parentId: category.parentId,
          // Add path property for better filtering
          path: category.name
        };
      });
      
      // Build the tree structure
      const rootNodes = [];
      
      // Assign children to their parents and update paths
      Object.values(categoryMap).forEach(category => {
        if (category.parentId && categoryMap[category.parentId]) {
          // This category has a parent, add it to the parent's children
          categoryMap[category.parentId].children.push(category);
          // Update path to include parent path
          category.path = `${categoryMap[category.parentId].path} > ${category.name}`;
        } else {
          // This is a root node
          rootNodes.push(category);
        }
      });
      
      // Sort each level alphabetically by name
      const sortByName = (a, b) => a.name.localeCompare(b.name);
      
      const sortChildren = (node) => {
        if (node.children && node.children.length > 0) {
          node.children.sort(sortByName);
          node.children.forEach(sortChildren);
        }
      };
      
      rootNodes.sort(sortByName);
      rootNodes.forEach(sortChildren);
      
      this.treeData = rootNodes;
    },
    filterNode(value, data) {
      if (!value) return true;
      
      // Search in the node's path (which includes all parent names)
      return data.path.toLowerCase().includes(value.toLowerCase());
    },
    setCategories(categories) {
      this.categories = categories;
    }
  }
}
</script>

<style scoped>
.category-selector {
  width: 100%;
}

:deep(.el-tree-node__content) {
  height: auto;
  padding: 4px 0;
}

:deep(.el-tree-node__label) {
  font-size: 14px;
}

:deep(.el-tree-node.is-disabled .el-tree-node__label) {
  color: #c0c4cc;
}

:deep(.el-tree--highlight-current .el-tree-node.is-current > .el-tree-node__content) {
  background-color: #f5f7fa;
}

:deep(.el-select-dropdown__wrap) {
  max-height: v-bind(maxHeight);
}

:deep(.el-tree-select__popper) {
  max-width: 500px;
}

:deep(.el-select-dropdown__item) {
  white-space: normal;
  word-break: break-word;
}
</style> 